import { supabase } from "../config/supabase";

/**
 * Feedback data interface
 */
export interface FeedbackData {
  who_are_you: string;
  use_case: string;
  referral_source: string;
}

/**
 * Feedback submission response interface
 */
export interface FeedbackResponse {
  success: boolean;
  error?: string;
  data?: any;
}

/**
 * Submit user feedback to the backend
 * @param data - The feedback data containing 3 required fields
 * @returns Promise with success status and optional error message
 */
export const submitFeedback = async (
  data: FeedbackData,
): Promise<FeedbackResponse> => {
  try {
    // Get the current session to ensure user is authenticated
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError || !session) {
      return {
        success: false,
        error: "You must be logged in to submit feedback",
      };
    }

    // Validate fields
    const errors: string[] = [];
    if (!data.who_are_you?.trim()) {
      errors.push("Who are you is required");
    }
    if (!data.use_case?.trim()) {
      errors.push("Use case is required");
    }
    if (!data.referral_source?.trim()) {
      errors.push("Referral source is required");
    }

    if (errors.length > 0) {
      return {
        success: false,
        error: errors.join("; "),
      };
    }

    // Insert directly into database (bypassing edge function)
    const { data: insertedData, error: insertError } = await supabase
      .from("user_feedback")
      .upsert(
        {
          user_id: session.user.id,
          who_are_you: data.who_are_you.trim(),
          use_case: data.use_case.trim(),
          referral_source: data.referral_source.trim(),
          submitted_at: new Date().toISOString(),
        },
        {
          onConflict: "user_id",
          ignoreDuplicates: false,
        },
      )
      .select();

    if (insertError) {
      console.error("Database error:", insertError);
      return {
        success: false,
        error: "Failed to save feedback. Please try again.",
      };
    }

    return {
      success: true,
      data: insertedData,
    };
  } catch (error: any) {
    console.error("Error submitting feedback:", error);
    return {
      success: false,
      error: error?.message || "An unexpected error occurred",
    };
  }
};

/**
 * Check if user has already submitted feedback
 * @returns Promise with boolean indicating if feedback was submitted
 */
export const checkFeedbackStatus = async (): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    // Use maybeSingle() to return null if no rows found, instead of throwing error
    const { data, error } = await supabase
      .from("user_feedback")
      .select("id")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Error checking feedback status:", error);
      return false;
    }

    // data will be null if no feedback found, or an object if found
    return !!data;
  } catch (error) {
    console.error("Error checking feedback status:", error);
    return false;
  }
};
